import React, { useRef, useEffect, useContext } from 'react';

import { Layout, BlueBox, BlurBox } from '@/components/general';
import { convertToNormal } from '@/utils/functions';
import { Links } from '@/utils/constants';
import * as styled from './Content.styled';
import { Tag, TagAttachment, TagVariants, TemplateCard } from './components';
import { FixedContext } from '../Wrapper';
import { Breadcrumbs } from '../general/Breadcrumbs';
import { Title, UnderTitle } from '../general/FormPages.styled';

interface IContent {
  data: any;
  currentCategory?: string;
  currentTechnology?: string;
  currentFeature?: string;
  currentLanguage?: string;
  setTitle?: React.Dispatch<React.SetStateAction<string>>;
  setDescription?: React.Dispatch<React.SetStateAction<string>>;
}

const Content: React.FC<IContent> = ({
  data,
  currentCategory,
  currentTechnology,
  currentFeature,
  currentLanguage,
  setTitle,
  setDescription,
}) => {
  const {
    title,
    description,
    categories,
    technologies,
    languages,
    features,
    templates,
  } = data;

  const tagsBox = useRef(null);

  const { isFixed, setIsFixed } = useContext(FixedContext);

  useEffect(() => {
    const addFixed = () => {
      if (window.innerWidth > 1200) {
        if (scrollY > tagsBox?.current?.offsetTop + 45) {
          setIsFixed(true);
        } else {
          setIsFixed(false);
        }
      } else {
        setIsFixed(false);
      }
    };

    window.addEventListener('scroll', addFixed);
    addFixed();

    return () => {
      window.removeEventListener('scroll', addFixed);
    };
  }, [tagsBox, tagsBox.current]);

  let filterTemplates: any[] = [];

  const filterTemplatesFunction = (
    currentValue,
    characteristicName,
    isFeature?,
  ) => {
    if (currentValue) {
      filterTemplates = templates.filter((template) =>
        template[characteristicName].find((characteristic) => {
          const characteristicValue = isFeature
            ? characteristic.feature.toLowerCase()
            : characteristic.toLowerCase();
          return (
            characteristicValue ===
            currentValue.replaceAll('-', ' ').replaceAll(' and ', ' & ')
          );
        }),
      );
    }
  };

  if (
    currentCategory ||
    currentTechnology ||
    currentFeature ||
    currentLanguage
  ) {
    filterTemplatesFunction(currentCategory, 'categories');
    filterTemplatesFunction(currentTechnology, 'technologies');
    filterTemplatesFunction(currentLanguage, 'languages');
    filterTemplatesFunction(currentFeature, 'features', true);
  } else {
    filterTemplates = templates;
  }

  const fixedTags = [
    {
      attachment: TagAttachment.CATEGORIES,
      text: currentCategory
        ? convertToNormal(currentCategory)
        : TagAttachment.CATEGORIES,
      restTags: categories,
      active: !!currentCategory,
    },
    {
      attachment: TagAttachment.TECHNOLOGIES,
      text: currentTechnology
        ? convertToNormal(currentTechnology)
        : TagAttachment.TECHNOLOGIES,
      restTags: technologies,
      active: !!currentTechnology,
    },
    {
      attachment: TagAttachment.LANGUAGES,
      text: currentLanguage
        ? convertToNormal(currentLanguage)
        : TagAttachment.LANGUAGES,
      restTags: languages,
      active: !!currentLanguage,
    },
    {
      attachment: TagAttachment.FEATURES,
      text: currentFeature
        ? convertToNormal(currentFeature)
        : TagAttachment.FEATURES,
      restTags: features,
      active: !!currentFeature,
    },
  ];

  const includeApp = (str: string) => {
    if (str.includes('app')) {
      return `${convertToNormal(str)} ${title.replace('App ', '')}`;
    }
    return `${convertToNormal(str)} ${title}`;
  };

  const pageTitle = currentCategory
    ? includeApp(currentCategory)
    : currentTechnology
    ? includeApp(currentTechnology)
    : currentLanguage
    ? includeApp(currentLanguage)
    : currentFeature
    ? includeApp(currentFeature)
    : title;

  useEffect(() => {
    setTitle(pageTitle);
  }, [pageTitle]);

  const breadcrumbs = [
    Links.TEMPLATES,
    convertToNormal(currentCategory),
    convertToNormal(currentTechnology),
    convertToNormal(currentLanguage),
    convertToNormal(currentFeature),
  ].filter((link) => link !== undefined);

  const pageDescription = currentCategory
    ? description.description.replace(
        'project',
        `${convertToNormal(currentCategory).toLocaleLowerCase()}`,
      )
    : currentTechnology
    ? description.description.replace(
        'project',
        `${convertToNormal(currentTechnology).toLocaleLowerCase()}`,
      )
    : currentFeature
    ? description.description.replace(
        'project',
        `${convertToNormal(currentFeature).toLocaleLowerCase()}`,
      )
    : description.description;

  useEffect(() => {
    setDescription(pageDescription);
  }, [pageDescription]);

  return (
    <Layout>
      <Breadcrumbs links={breadcrumbs} />
      <BlurBox $containTagsBox>
        <styled.TopBoxDescription>
          <Title>{pageTitle}</Title>
          <UnderTitle style={{ maxWidth: '503px' }}>
            {pageDescription}
          </UnderTitle>
          <styled.LearnMore to={`${Links.TEMPLATES}/about`}>
            Learn more about App Templates
          </styled.LearnMore>
        </styled.TopBoxDescription>
        <styled.TagsSection>
          <styled.TagsBox ref={tagsBox}>
            <Tag
              $active={
                !currentCategory &&
                !currentTechnology &&
                !currentLanguage &&
                !currentFeature
              }
              variant={TagVariants.BIG}
            >
              All
            </Tag>
            {fixedTags.map((tag) => (
              <Tag
                key={tag.attachment}
                variant={TagVariants.DROPDOWN}
                restTags={tag.restTags}
                attachment={tag.attachment}
                $active={tag.active}
              >
                {tag.text}
              </Tag>
            ))}
          </styled.TagsBox>
          <styled.FixedTagsBox $fixed={isFixed}>
            <Tag
              $active={
                !currentCategory &&
                !currentTechnology &&
                !currentLanguage &&
                !currentFeature
              }
              $fixedTagBox
              fixedTime={0.25}
              $fixed={isFixed}
              variant={TagVariants.BIG}
            >
              All
            </Tag>
            {fixedTags.map((tag, index) => (
              <Tag
                key={tag.attachment}
                variant={TagVariants.DROPDOWN}
                $fixedTagBox
                fixedTime={(index + 3) / 4}
                $fixed={isFixed}
                restTags={tag.restTags}
                attachment={tag.attachment}
                $active={tag.active}
              >
                {tag.text}
              </Tag>
            ))}
          </styled.FixedTagsBox>
        </styled.TagsSection>
      </BlurBox>
      <styled.TemplatesBox>
        {filterTemplates.map((template) => (
          <TemplateCard key={template.id} template={template} />
        ))}
      </styled.TemplatesBox>
      <BlueBox
        title="Didn't find the app template you want?"
        description="Talk to our experts for a quick consultation, template request, custom design or development"
        $hasCaption={false}
      />
    </Layout>
  );
};

export default Content;
